import { createFileRoute, Link } from "@tanstack/react-router";
import { BruloHeader } from "@/components/brulo-header";
import { ArrowLeft, Shield, User, Lock, Share2, FileText, HeartPulse } from "lucide-react";

export const Route = createFileRoute("/privacidad")({
  head: () => ({
    meta: [
      { title: "Aviso de Privacidad — Brulo" },
      { name: "description", content: "Aviso de privacidad de Brulo. Conoce cómo protegemos y tratamos tus datos personales." },
      { property: "og:title", content: "Aviso de Privacidad — Brulo" },
      { property: "og:description", content: "Aviso de privacidad de Brulo. Conoce cómo protegemos y tratamos tus datos personales." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PrivacidadPage,
});

function PrivacidadPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <BruloHeader />

      <main className="flex-1 px-4 sm:px-6 py-10 sm:py-16">
        <div className="mx-auto max-w-2xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Regresar
          </Link>

          <h1 className="font-display text-4xl sm:text-5xl mb-2">AVISO DE PRIVACIDAD</h1>
          <p className="text-sm text-muted-foreground mb-10">
            Última actualización: 21 de agosto de 2026
          </p>

          <div className="space-y-10">
            <section className="bg-card border border-border rounded-lg p-6 sm:p-8">
              <p className="text-foreground leading-relaxed">
                <strong>Brulo</strong> es un proyecto operado por <strong>Vladimir Salgado</strong>, persona física, con domicilio en{" "}
                <strong>Aguascalientes, Ags., México</strong>, y plataforma disponible en{" "}
                <a
                  href="https://brulo.fit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  brulo.fit
                </a>
                . Brulo es responsable del tratamiento de sus datos personales conforme a la{" "}
                <em>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</em>{" "}
                y la <em>Ley General de Protección de Datos Personales en Posesión de Sujetos Obligados</em>, en lo conducente.
              </p>
              <p className="text-foreground leading-relaxed mt-4">
                Para ejercer sus derechos ARCO (Acceso, Rectificación, Cancelación y Oposición), escriba a:{" "}
                <a
                  href="mailto:privacidad@brulo.fit"
                  className="text-primary hover:underline"
                >
                  privacidad@brulo.fit
                </a>
              </p>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <User className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">¿Qué datos recopilamos?</h2>
              </div>
              <ul className="bg-card border border-border rounded-lg p-6 sm:p-8 space-y-3 list-disc list-inside text-foreground leading-relaxed">
                <li>Nombre completo</li>
                <li>Correo electrónico</li>
                <li>Número de teléfono (WhatsApp)</li>
                <li>Ciudad y estado de residencia</li>
                <li>Disciplinas deportivas que practica</li>
                <li>
                  Información del negocio (para Locales Brulo): nombre, dirección, horarios, descripción
                </li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <HeartPulse className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">Datos personales sensibles (Ficha de Emergencia)</h2>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8 text-foreground leading-relaxed space-y-4">
                <p>
                  Además de los datos mencionados anteriormente, Brulo ofrece una función opcional de Ficha de Emergencia. Si decides usarla, recabaremos los siguientes datos personales sensibles relacionados con tu salud:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-1">
                  <li>Tipo de sangre</li>
                  <li>Alergias</li>
                  <li>Condiciones médicas</li>
                  <li>Medicamentos de uso regular</li>
                  <li>Aseguradora y número de póliza</li>
                  <li>Hospital de preferencia</li>
                  <li>Notas médicas adicionales</li>
                  <li>Datos de tus contactos de emergencia (nombre, parentesco y teléfono)</li>
                </ul>
                <p>
                  <strong>Carácter voluntario.</strong> Proporcionar estos datos es completamente opcional. Puedes usar Brulo sin llenar tu Ficha de Emergencia. Al capturarlos y guardarlos, otorgas tu consentimiento expreso para su tratamiento conforme a este aviso.
                </p>
                <p>
                  <strong>Finalidad.</strong> Estos datos se tratan con la única finalidad de permitir que, en caso de un percance durante una rodada o evento, las personas organizadoras y de apoyo ("barredoras") puedan acceder a tu información médica y contactar a tus familiares para brindarte auxilio oportuno.
                </p>
                <p>
                  <strong>Con quién se comparten y bajo qué condiciones.</strong> Tu Ficha de Emergencia solo es visible para las personas organizadoras y barredoras de un evento al que te hayas inscrito y para el cual hayas autorizado expresamente el acceso, y únicamente durante una ventana de tiempo limitada alrededor del evento (desde una hora antes de su inicio y por algunas horas posteriores). Fuera de esa ventana, o para eventos donde no hayas dado tu autorización, tus datos de salud no son accesibles para nadie más que tú.
                </p>
                <p>
                  <strong>Control y revocación.</strong> Puedes consultar, modificar o eliminar tu Ficha de Emergencia en cualquier momento desde tu perfil. Asimismo, puedes otorgar o revocar tu autorización de acceso para cada evento de forma independiente y en cualquier momento. La revocación surte efecto de inmediato.
                </p>
                <p>
                  <strong>Registro de accesos.</strong> Brulo mantiene un registro de qué personas han consultado tu Ficha de Emergencia, disponible para tu consulta desde tu perfil, como medida de transparencia.
                </p>
                <p>
                  <strong>Conservación.</strong> Tus datos de salud se conservan mientras tu cuenta permanezca activa y se eliminan de forma permanente cuando eliminas tu cuenta.
                </p>
                <p>
                  <strong>Transferencias.</strong> Brulo no transfiere, vende ni comparte tus datos de salud con terceros distintos a las personas organizadoras y barredoras descritas. Si en el futuro se integran servicios adicionales (por ejemplo, servicios de emergencia), se te informará y se solicitará tu consentimiento antes de cualquier tratamiento nuevo.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Share2 className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">¿Para qué usamos sus datos?</h2>
              </div>
              <ul className="bg-card border border-border rounded-lg p-6 sm:p-8 space-y-3 list-disc list-inside text-foreground leading-relaxed">
                <li>Crear y gestionar su cuenta en Brulo</li>
                <li>Enviar recordatorios de eventos por WhatsApp</li>
                <li>Mostrar eventos y cafeterías cercanas a su ubicación</li>
                <li>Mejorar la experiencia de la plataforma</li>
                <li>
                  Enviar comunicaciones relacionadas con Brulo{" "}
                  <span className="text-muted-foreground">(puede cancelarlas en cualquier momento)</span>
                </li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">¿Con quién compartimos sus datos?</h2>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8 text-foreground leading-relaxed">
                <p>
                  Sus datos <strong>no se venden ni se comparten con terceros</strong>, salvo proveedores tecnológicos necesarios para operar la plataforma (Supabase, Vercel), quienes están sujetos a sus propias políticas de privacidad.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">¿Cómo protegemos sus datos?</h2>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8 text-foreground leading-relaxed">
                <p>
                  Utilizamos medidas técnicas y organizativas para proteger su información contra acceso no autorizado, pérdida o alteración.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">Sus derechos (ARCO)</h2>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8 text-foreground leading-relaxed">
                <p>
                  Tiene derecho a <strong>Acceder, Rectificar, Cancelar u Oponerse</strong> al tratamiento de sus datos personales escribiendo a:{" "}
                  <a
                    href="mailto:privacidad@brulo.fit"
                    className="text-primary hover:underline"
                  >
                    privacidad@brulo.fit
                  </a>
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="font-display text-2xl">Cambios a este aviso</h2>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8 text-foreground leading-relaxed">
                <p>
                  Cualquier modificación será publicada en esta página. La fecha de última actualización es: <strong>21 de agosto de 2026</strong>.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
